<template>
	<page-container :page="page">
		<heading-image
			v-if="page.headingImage"
			:heading-image="{
				image: page.headingImage,
				imageWebp: page.headingImageWebp,
				imagePortrait: page.headingImagePortrait,
				imagePortraitWebp: page.headingImagePortraitWebp,
			}"
		/>
		<breadcrumbs
			:breadcrumbs="page.breadcrumbs"
			:class="page.headingImage ? '' : 'no-slider'"
			:home="defaults[locale].website.hotelName"
		/>

		<main-content :title="page.title || page.header" :content="page.content" />

		<section class="rooms-items">
			<div v-for="room in roomsData" :key="room.pageID" class="columns column4">
				<div class="rooms-content-overlay">
					<h3>{{ room.header }}</h3>
					<div class="room-icon">
						<div v-if="room.pax">
							<font-awesome-icon :icon="`fal fa-users`" size="1x" />
							<span>{{ room.pax }}</span>
						</div>
						<div v-if="room.surface">
							<font-awesome-icon :icon="`fal fa-ruler-combined`" size="1x" />
							<span>{{ room.surface }}</span>
						</div>
					</div>
					<nuxt-link :to="localePath(`/${room.filename}`)" class="button button-no-fill">
						{{ defaults[locale].rooms.view }}
					</nuxt-link>
					<book-button class="button button-cta" :room-i-d="room.bookingID">
						{{ $t('bookThisRoom') }}
					</book-button>
				</div>
				<picture>
					<source v-if="room.imageOverviewWebp" :srcset="room.imageOverviewWebp" type="image/webp" />
					<source :srcset="room.imageOverview" />
					<img :src="room.imageOverview" :alt="room.imageOverviewAlt" class="room-item-img" loading="lazy" />
				</picture>
			</div>
			<div v-if="defaults[locale].rooms.imageOverview" class="columns column4">
				<div class="rooms-content-overlay">
					<div v-parse-links v-html="defaults[locale].rooms.overviewContent" />
					<nuxt-link
						v-if="defaults[locale].rooms.button"
						:to="defaults[locale].rooms.link"
						class="button button-cta"
					>
						{{ defaults[locale].rooms.more }}
					</nuxt-link>
				</div>
				<picture>
					<source
						v-if="defaults[locale].rooms.imageOverviewWebp"
						:srcset="defaults[locale].rooms.imageOverviewWebp"
						type="image/webp"
					/>
					<source :srcset="defaults[locale].rooms.imageOverview" />
					<img
						:src="defaults[locale].rooms.imageOverview"
						:alt="defaults[locale].rooms.imageOverviewAlt"
						class="room-item-img"
						loading="lazy"
					/>
				</picture>
			</div>
		</section>

		<contentblocks v-if="page && page.contentblocks && page.contentblocks.length" :items="page.contentblocks" />

		<review-slider v-if="page && page.showReviewSlider" :header="defaults[locale].website.reviewsHeader" />
		<frequently-asked-questions
			v-if="page && page.frequentlyAskedQuestion"
			:header="page.frequentlyAskedQuestion.header"
			:items="page.frequentlyAskedQuestion.faqitems"
		/>
		<pre-footer v-if="page && page.footerSection.length" :footer-section="page.footerSection[0]" />
		<newsletter
			v-if="page && page.showNewsletter"
			:header="defaults[locale].website.newsletterHeader"
			:content="defaults[locale].website.newsletterContent"
			:button="defaults[locale].website.newsletterButton"
		/>
	</page-container>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { defaults, fetchDefaults } = useDefaults();
const { locale } = useI18n();

await fetchPage();
await fetchDefaults();

const { data: roomsData } = await useWebsiteFetch('rooms', {
	query: { language: locale.value },
	key: `${locale.value}/rooms`,
});
</script>

<style lang="scss" scoped>
.rooms-items {
	padding: 0;
	display: flex;
	flex-flow: row wrap;
	align-items: stretch;
	justify-content: flex-start;
	background: $alternating-background-color;

	.column4 {
		padding: 0;
	}

	h3 {
		color: #fff;
	}

	.button-no-fill {
		border-color: #fff;
		color: #fff;

		&:hover {
			color: #ff8500;
			border-color: #ff8500;
		}
	}
}

.room-icon {
	display: flex;
	flex-flow: row wrap;
	align-items: center;
	justify-content: center;
	gap: 20px;
	font-size: 16px;
	margin: 0 0 15px;

	svg,
	i {
		font-size: 13px;
		margin: 0 3px 0 0;
	}
}

.rooms-content-overlay {
	position: absolute;
	left: 0;
	top: 50%;
	width: 100%;
	transform: translate(0, -50%);
	color: #fff;
	z-index: 3;
	padding: 30px;
	text-align: center;
}

.rooms-items .column4::before {
	content: '';
	display: block;
	background: rgba(0 0 0 / 30%);
	z-index: 2;
	position: absolute;
	inset: 0;
}

.room-item-img {
	width: 100%;
	height: 100%;
	min-height: 400px;
	z-index: 1;
}

.bg-image-content {
	background: $contentblock-background-color;
	padding: 50px;
	z-index: 3;
}

.slides {
	position: absolute;
	left: 0;
	top: 0;
	width: 100%;
	height: 100%;

	img {
		height: 100% !important;
		width: 100%;
		object-fit: cover;
	}

	li {
		position: relative;
		width: 100%;
		height: 100%;

		&::before {
			content: '';
			position: absolute;
			inset: 0;
			background: rgb(0 0 0 / 10%);
			z-index: 2;
		}
	}
}

@media (max-width: 800px) {
	.rooms-items {
		position: relative;
		padding: 0;
	}

	.bg-image-content {
		padding: 40px;
		width: 100%;
	}
}

@media (max-width: 600px) {
	.rooms-items {
		.column4 {
			margin: 3px 0;
		}
	}
}
</style>
